import React, { useEffect, useState } from 'react';
import { DataGrid, showNotif } from '@p81-common/ui';

import InputSearch from 'components/common/forms/InputSearch/InputSearch';

import { useDataGrid } from 'hooks/useDataGrid';
import { getRowHeight } from 'utils/common';

import classes from '../PlatformFeatureFlags.module.scss';
import createClasses from '../styles';
import { activityLogConfig, defaultFilters } from '../helpers/tableConfig';
import { errorReason, fetchActivityLog } from '../helpers/api';
import type { ActivityLogResponse } from '../helpers/types';

const ActivityLogTab = ({ customerId }: { customerId: string }) => {
  const styles = createClasses();

  const {
    setFilter,
    filter,
    tableData,
    setTableData,
    tableConfigDisplay,
    numOfRowsOptions,
    loaderTable,
    setLoaderTable
  } = useDataGrid(activityLogConfig, defaultFilters);

  const [search, setSearch] = useState('');
  const [logs, setLogs] = useState<ActivityLogResponse>();

  useEffect(() => {
    let cancelled = false;
    setLoaderTable(true);
    fetchActivityLog(
      customerId,
      search,
      filter.currentPage,
      filter.itemsPerPage
    )
      .then(body => {
        if (cancelled) return;
        setLogs(body);
        setTableData(body.data);
      })
      .catch(err => {
        if (cancelled) return;
        // Was a console.log behind an eslint-disable: a failed fetch rendered
        // as an empty log, which reads as "nothing ever happened".
        showNotif(`Could not load the activity log: ${errorReason(err)}`, {
          alertProps: { severity: 'error' }
        });
      })
      .finally(() => {
        !cancelled && setLoaderTable(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, filter, search]);

  return (
    <>
      {/* Search on feature name is the whole of it, so this tab has no
          Filter button and needs no options endpoint. */}
      <div className={classes.filters_main}>
        <InputSearch
          placeholder="Search by feature name"
          value={search}
          onChange={(e: any) => {
            setSearch(e.target.value);
            setFilter({ ...filter, currentPage: 1 });
          }}
          onClearSearch={() => setSearch('')}
          disabled={loaderTable}
        />
      </div>

      <DataGrid
        getRowId={entry => entry._id}
        autoHeight
        lazyLoading
        loading={loaderTable}
        tableConfig={tableConfigDisplay}
        numOfRowsOptions={numOfRowsOptions}
        classes={{ container: styles.grid }}
        getRowHeight={getRowHeight}
        tableData={{
          page: logs?.page || 0,
          totalPages: logs?.totalPages || 0,
          totalRecords: logs?.totalRecords || 0,
          data: tableData || []
        }}
        paginationProps={{
          page: filter.currentPage - 1,
          count: logs?.totalRecords || 0,
          rowsPerPage: filter.itemsPerPage,
          onChangePage: (event, page) =>
            setFilter({ ...filter, currentPage: page + 1 }),
          hideNavigation: !logs?.totalRecords,
          hideRowCount: !logs?.totalRecords
        }}
        toolbarProps={{
          title1: 'Activity Log:',
          // Unlike the flags tab, this count is simply the number of activities.
          title2: `(${logs?.totalRecords || 0})`
        }}
        rowCount={logs?.totalRecords || 0}
        sortingMode={'server'}
        paginationMode={'server'}
      />
    </>
  );
};

export default ActivityLogTab;
